import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    inject,
    Injector,
    Input,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { ICardCatalogue } from '../../../_shared/models/card/card-catalogue.model';
import { ICard } from '../../../_shared/models/card/card.model';
import { IDynamicElement } from '../../../_shared/models/dynamic/dynamic-element.model';
import { IFiltersActivated } from '../../../_shared/models/filters-activated.model';
import { FiltersLatestActivation } from '../../../_shared/models/filters-latest-activation.model';
import { Filters } from '../../../_shared/models/filters.model';
import { PAGE_SERVICE } from '../../../_shared/models/page-service';
import { IQueryParams } from '../../../_shared/models/page-service/query-params.model';
import { Paging } from '../../../_shared/models/paging.model';
import { Search } from '../../../_shared/models/search.model';
import { Sorting } from '../../../_shared/models/sorting.model';
import { CardCatalogueDataSource } from '../../../_shared/types/card-grid-data-source.type';
import { BadgeComponent } from '../../../badge/badge.component';
import { ButtonComponent } from '../../../button/button.component';
import { FilterDropdownComponent } from '../../../filter/filter-dropdown/filter-dropdown.component';
import { FilterMobileComponent } from '../../../filter/filter-mobile/filter-mobile.component';
import { FilterComponent } from '../../../filter/filter.component';
import { GridComponent } from '../../../grid/grid.component';
import { IconComponent } from '../../../icon/icon.component';
import { PaginationComponent } from '../../../pagination/pagination.component';
import { SearchComponent } from '../../../search/search.component';
import { CardGridComponent } from '../card-grid/card-grid.component';
import { CardCatalogueTrackingService } from './card-catalogue.tracking';

@Component({
    selector: 'sirius-card-catalogue',
    templateUrl: './card-catalogue.component.html',
    styleUrls: ['./card-catalogue.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        GridComponent,
        SearchComponent,
        CommonModule,
        FilterComponent,
        TranslateModule,
        IconComponent,
        FilterDropdownComponent,
        CardGridComponent,
        PaginationComponent,
        FilterMobileComponent,
        ButtonComponent,
        BadgeComponent,
    ],
    providers: [CardCatalogueTrackingService],
})
export class CardCatalogueComponent implements OnInit, IDynamicElement {
    private _pageService = inject(PAGE_SERVICE);

    private static nextId = 0;

    @Input() items: ICard[] = [];
    @Input() filters?: Filters;
    @Input() paging?: Paging;
    @Input() sorting?: Sorting;
    @Input() searchQuery?: Search;
    @Input() additionalQueryParams?: IQueryParams[];
    @Input() dataSource: CardCatalogueDataSource = 'other';

    @ViewChild(SearchComponent) searchComponent?: SearchComponent;
    @ViewChild('sortingDropdown') sortingDropdownComponent?: FilterDropdownComponent;
    @ViewChild('sortingDropdown', { read: ElementRef }) sortingDropdownElement?: ElementRef<HTMLElement>;
    @ViewChild('catalogue', { read: ElementRef }) private _catalogue!: ElementRef<HTMLElement>;

    config?: ICardCatalogue;
    mobileMenuOpen = false;
    updating = new BehaviorSubject<boolean>(false);
    showSortingOptions = false;
    selectedSortingLabel = 'Default';
    readonly instanceId = CardCatalogueComponent.nextId++;
    readonly sortingDropdownId = `sir-sorting-dropdown-${this.instanceId}`;
    readonly sortingTriggerId = `sir-sorting-trigger-${this.instanceId}`;

    private _scroll = new Subject<void>();
    private _select = new Subject<[ICard, number]>();
    private _filter = new Subject<Filters>();
    private _activation = new Subject<IFiltersActivated>();
    private _lastActivatedFilters?: IFiltersActivated;
    private _trackingService = inject(CardCatalogueTrackingService);
    private _elementRef = inject(ElementRef);
    private readonly _injector = inject(Injector);
    private readonly _platformId = inject(PLATFORM_ID);

    set pageSize(value: number) {
        if (this.paging) {
            this.paging.pageSize = value;
            this.resetPaging();
        }
    }

    @HostListener('window:scroll', [])
    onScroll(): void {
        if (!isPlatformBrowser(this._platformId)) return;
        this._scroll.next();
    }

    @HostListener('document:click', ['$event'])
    onClickOutsideSorting(event: Event): void {
        if (!isPlatformBrowser(this._platformId)) return;
        if (this.showSortingOptions) {
            const el = this.sortingDropdownElement?.nativeElement;
            const target = event.target as Node | null;
            if (el && target && !el.contains(target)) {
                this.showSortingOptions = false;
            }
        }
    }

    ngOnInit(): void {
        if (this.config) {
            this.dataSource =
                this.config.dataSources?.length === 1
                    ? (this.config.dataSources[0] as CardCatalogueDataSource)
                    : 'other';
            this.items = this.config.result.items;
            this.filters = new Filters(this.config.result.filters);
            this.paging = new Paging(this.config.result.paging);
            this.searchQuery = new Search(this.config.result.searchQuery);

            if (this.config.result.sorting && this.config.result.sorting.length > 0) {
                this.sorting = new Sorting(this.config.result.sorting);
            }
        }

        this.setSortingLabel();
        this._trackingService.trackScroll(this._elementRef, this._scroll, this.dataSource);
        this._trackingService.trackSelect(this._select, this.dataSource);
        this._trackingService.trackFilter(this._filter, this.dataSource);
        this._trackingService.trackActivate(this._activation);
        this._scroll.next();
    }

    resetPaging() {
        if (this.paging) {
            this.paging.currentPage = 1;
        }
    }

    openMobileMenu(value: boolean): void {
        this.mobileMenuOpen = value;
    }

    update(): Observable<unknown> {
        const params: IQueryParams[] = [];

        if (this.paging) {
            params.push(...this.paging.generateParams());
        }
        if (this.filters) {
            params.push(this.filters.generateParams());

            // If overall filters are empty,
            // we dont need to handle the last activated filters
            if (this._lastActivatedFilters) {
                const lastActivatedFilters = new FiltersLatestActivation(this._lastActivatedFilters);

                params.push(lastActivatedFilters.generateParams());
            }
        }
        if (this.sorting) {
            params.push(this.sorting.generateParams());
        }
        if (this.searchQuery) {
            params.push(this.searchQuery.generateParams());
        }
        if (this.additionalQueryParams) {
            params.push(...this.additionalQueryParams);
        }

        return this._pageService.updatePage(params.filter((x: IQueryParams) => x.params.length));
    }

    onFiltersActivated(activated: IFiltersActivated) {
        this._lastActivatedFilters = activated;
        this._activation.next(activated);
    }

    onFilter(filters: Filters): void {
        this.filters = filters;

        this.resetPaging();
        this.update().subscribe(() => this._filter.next(filters));
    }

    onPaging(paging: Paging) {
        this.paging = paging;
        this._catalogue.nativeElement.scrollIntoView();

        this.update().subscribe();
    }

    onSearch(val: string): void {
        this.searchQuery = new Search(val);

        this.resetPaging();
        this.updating.next(true);
        this.update().subscribe(() => this.updating.next(false));
    }

    onSelect(item: [ICard, number]): void {
        this._select.next([item[0], item[1]]);
    }

    onSort(): void {
        this.showSortingOptions = false;

        if (!this.sorting || this.sorting.options.length === 0) {
            return;
        }

        const opts = this.sorting.options;
        this.selectedSortingLabel = opts.find((opt) => opt.active)?.name ?? 'Default';

        this.update().subscribe();
    }

    onSortingDropdownClose(trigger: HTMLButtonElement): void {
        this.showSortingOptions = false;
        if (isPlatformBrowser(this._platformId)) {
            trigger.focus();
        }
    }

    onSortingTriggerKeydown(event: KeyboardEvent, trigger: HTMLButtonElement): void {
        switch (event.key) {
            case 'ArrowDown':
            case 'Home':
                event.preventDefault();
                this.openSortingAndFocus('first');
                return;
            case 'ArrowUp':
            case 'End':
                event.preventDefault();
                this.openSortingAndFocus('last');
                return;
            case 'Enter':
            case ' ':
            case 'Spacebar':
                event.preventDefault();
                if (this.showSortingOptions) {
                    this.showSortingOptions = false;
                } else {
                    this.openSortingAndFocus('first');
                }
                return;
            case 'Escape':
                if (!this.showSortingOptions) return;
                event.preventDefault();
                this.showSortingOptions = false;
                if (isPlatformBrowser(this._platformId)) {
                    trigger.focus();
                }
                return;
            default:
                return;
        }
    }

    setSortingLabel(): void {
        if (!this.sorting || this.sorting.options.length === 0) {
            return;
        }

        const opts = this.sorting.options;
        this.selectedSortingLabel = opts.find((opt) => opt.active)?.name ?? opts[0].name;
    }

    toggleSortingOptions(event: Event): void {
        this.showSortingOptions = !this.showSortingOptions;
        event.stopPropagation();
    }

    private openSortingAndFocus(target: 'first' | 'last'): void {
        this.showSortingOptions = true;
        if (!isPlatformBrowser(this._platformId)) return;
        afterNextRender(
            () => {
                if (!this.sortingDropdownComponent) return;

                if (target === 'last') {
                    this.sortingDropdownComponent.focusLastOption();
                    return;
                }

                this.sortingDropdownComponent.focusFirstActiveOrFirstOption();
            },
            { injector: this._injector }
        );
    }
}
